import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, useAuth } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, Users, Clock, Star, Crown, CheckCircle, PlayCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

export default function CourseDetails() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [creator, setCreator] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [enrolled, setEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourseDetails();
  }, [courseId]);

  const fetchCourseDetails = async () => {
    try {
      const courseRes = await axios.get(`${API}/courses/${courseId}`);
      setCourse(courseRes.data);
      
      // Get creator info
      const creatorRes = await axios.get(`${API}/users?limit=1000`);
      const creatorData = creatorRes.data.find(u => u.id === courseRes.data.creator_id);
      setCreator(creatorData);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Course not found');
      navigate('/courses');
    }
  };

  const handleEnroll = () => {
    toast.info('Course enrollment with PayPal coming soon!');
  };

  if (loading || !course) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Course Header */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Badge className="mb-3">{course.category}</Badge>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {course.title}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
              {course.description}
            </p>

            {/* Stats */}
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-600" />
                <span>{course.enrollment_count} students</span>
              </div>
              {course.rating_avg > 0 && (
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  <span>{course.rating_avg.toFixed(1)} ({course.total_ratings} reviews)</span>
                </div>
              )}
            </div>

            {/* Creator */}
            {creator && (
              <div className="flex items-center gap-3 mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={creator.picture} />
                  <AvatarFallback className="bg-purple-100 text-purple-600">
                    {creator.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Created by</p>
                  <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    {creator.name}
                    <Badge className="bg-purple-100 text-purple-700 border-0">
                      <Crown className="h-3 w-3 mr-1" />
                      Creator
                    </Badge>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Enroll Card */}
          <div>
            <Card className="p-6 sticky top-6 dark:bg-gray-800">
              <div className="text-center mb-6">
                <p className="text-4xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                  {course.price_gbp === 0 ? 'Free' : `£${course.price_gbp.toFixed(2)}`}
                </p>
                {course.price_gbp > 0 && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">One-time payment</p>
                )}
              </div>

              <Button
                onClick={handleEnroll}
                className="w-full bg-purple-500 hover:bg-purple-600 text-white mb-4"
                disabled={enrolled}
              >
                {enrolled ? 'Enrolled' : course.price_gbp === 0 ? 'Enroll Free' : 'Enroll Now'}
              </Button>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Lifetime access</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>100% money-back guarantee</span>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Course Content Preview */}
        <Card className="p-8 dark:bg-gray-800">
          <h2 className="text-2xl font-bold mb-6 dark:text-white">Course Content</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Full course content available after enrollment
          </p>
        </Card>
      </div>
    </Layout>
  );
}
